/**
 * General & Slots — the four step cards, with truthful collapsed summaries
 * computed from the live draft.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useState } from 'react';
import { Box, CalendarClock, RadioTower, Rows3 } from 'lucide-react';

import { StepCard, StepCardGroup, Switch } from '@/components/ui';

import type { DropGenPlanDetail } from '../../../drop-generator.types';
import type { ContainerEditor } from '../state/useContainerEditor';
import { HitAgainCard } from '../verdicts/cards';
import { ContainerStep } from './ContainerStep';
import { useGeneralTimes } from './hooks/use-general-times';
import { useOutsideRemoval } from './hooks/use-outside-removal';
import { HourOptimizerStep } from './optimizer/HourOptimizerStep';
import { ScheduleStep } from './schedule/ScheduleStep';
import { scheduleSummary, slotsSummary } from './slots/step-summaries';
import { SlotsStep } from './SlotsStep';
import { RouteTestStep } from '../route-test/RouteTestStep';
import { RefreshListsButton } from '../groups/repick/RefreshListsButton';

interface Props {
  editor: ContainerEditor;
  plan: DropGenPlanDetail;
  containerId: string | null;
}

export const GeneralSlotsTab: FC<Props> = ({ editor, plan, containerId }) => {
  const { draft, setField } = editor;
  const { groupRefs, times, previous, timesByKey, slotMinutes, slotLabels } = useGeneralTimes(draft, plan, containerId);
  const outside = useOutsideRemoval(draft, plan, containerId);
  const [open, setOpen] = useState<number | null>(null);
  const routeTest = draft.kind === 'ROUTE_TEST';
  const slotsIndex = routeTest ? 3 : 4;

  return (
    <StepCardGroup openIndex={open} onOpenChange={setOpen}>
      <StepCard
        index={1}
        icon={Box}
        title="Container"
        summary={`${draft.name.trim() || 'name not set'} · ${draft.campaignWiserId ? `campaign ${draft.campaignWiserId}` : 'campaign not set'}`}
        headerRight={
          <Switch checked={draft.enabled} onCheckedChange={(enabled) => setField('enabled', enabled)} aria-label="Container enabled" />
        }
      >
        <ContainerStep editor={editor} countryKey={plan.countryKey} />
      </StepCard>
      <StepCard index={2} icon={CalendarClock} title="Schedule" summary={scheduleSummary(draft)}>
        <ScheduleStep
          editor={editor}
          plan={plan}
          startMin={times?.startMin ?? null}
          endMin={times?.endMin ?? null}
          slotMinutes={slotMinutes}
          slotLabels={slotLabels}
          spanEndMin={times && slotMinutes.length > 0 ? times.lastPlannedMin : null}
          previous={previous}
          cut={(times?.slotTimes ?? []).filter((t) => t.clamped).length}
          outside={(times?.slotTimes ?? []).filter((t) => t.outsideWindow).length}
          sentOutside={(times?.slotTimes ?? []).filter((t) => t.sentOutside).length}
          routeTest={editor.draft.kind === 'ROUTE_TEST'}
          onOpenDrops={() => setOpen(slotsIndex)}
        />
      </StepCard>
      {routeTest ? null : (
        <HourOptimizerStep
          index={3}
          editor={editor}
          countryKey={plan.countryKey}
          startMin={times?.startMin ?? null}
          endMin={times?.endMin ?? null}
          slotMinutes={slotMinutes}
          windowStartMin={plan.windowStartMin}
          windowEndMin={plan.windowEndMin}
        />
      )}
      <StepCard
        index={slotsIndex}
        icon={Rows3}
        title="Drop slots"
        summary={slotsSummary(draft)}
        headerRight={containerId ? <RefreshListsButton countryKey={plan.countryKey} containerId={containerId} /> : undefined}
      >
        <SlotsStep
          editor={editor}
          countryKey={plan.countryKey}
          containerId={containerId}
          timesByKey={timesByKey}
          groupRefs={groupRefs}
          startMin={times?.startMin ?? null}
          endMin={times?.endMin ?? null}
          windowEndMin={plan.windowEndMin}
          outside={outside}
        />
      </StepCard>
      {routeTest ? (
        <StepCard
          index={4}
          icon={RadioTower}
          title="Route test"
          summary={draft.gateway.ranking.source === 'gateway' ? 'gateway stats, today' : 'drop stats, today'}
        >
          <RouteTestStep editor={editor} plan={plan} containerId={containerId} />
        </StepCard>
      ) : (
        <HitAgainCard index={5} editor={editor} plan={plan} containerId={containerId} />
      )}
    </StepCardGroup>
  );
};
